import React from 'react';
import { getIn, useFormikContext } from 'formik';

import { COUNTRY_TO_STATE_MAP } from '~/Types';
import { isCountryWithStates } from '~/Utils';

interface PolicyStateCountrySyncProps {
  country?: string | null;
}

// Returns the policy_state that is valid for `country`: null when the country has no states,
// or when the held state isn't one of that (state-bearing) country's states; otherwise the
// value unchanged. Shared by the sync effect and by each dialog's submit/validation paths so
// a stale value can't slip through the window before the effect runs (the effect keeps Formik
// as the source of truth for the live UI, but it fires asynchronously — validation or submit
// can run first, so they must normalise too rather than trust the field is already clean).
export function normalizePolicyState<T extends string | null | undefined>(
  country: string | null | undefined,
  state: T
): T | null {
  if (!country) return state ?? null;
  if (!isCountryWithStates(country)) return null;
  const statesForCountry = COUNTRY_TO_STATE_MAP[country as keyof typeof COUNTRY_TO_STATE_MAP];
  return state && !(state in statesForCountry) ? null : (state ?? null);
}

// Keeps policy_state consistent with the selected country by clearing a stale value. Runs as an
// effect (not a render-time mutation) so Formik stays the source of truth. Shared between
// EditPolicyDialog (keys off policy_country) and HomeManualPolicyDetailsDialog (keys off the
// insured property-location country) so the clearing semantics can't drift.
function PolicyStateCountrySync({ country }: PolicyStateCountrySyncProps) {
  const { values, setFieldValue } = useFormikContext();
  const policyState = getIn(values, 'policy_state');
  React.useEffect(() => {
    if (!country) return;
    const normalized = normalizePolicyState(country, policyState);
    // Only write when we're clearing a held value; don't seed null onto an untouched field.
    if (normalized !== policyState && policyState !== null && policyState !== undefined) {
      setFieldValue('policy_state', normalized);
    }
  }, [country, policyState, setFieldValue]);
  return null;
}

export default PolicyStateCountrySync;
